"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { SkillCategory } from "@/data/types";

interface SkillShowcaseProps {
  title: string;
  categories: SkillCategory[];
}

export function SkillShowcase({ title, categories }: SkillShowcaseProps) {
  const [activeCategory, setActiveCategory] = useState(0);

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 15 }
    }
  };

  const tabVariants = {
    inactive: {
      opacity: 0.6,
      scale: 0.95,
      y: 0
    },
    active: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 }
    }
  };

  return (
    <motion.section
      className="mt-16 mb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4, duration: 0.8 }}
    >
      <motion.h2
        className="text-2xl font-bold text-[#88ff88] mb-6"
        variants={itemVariants}
      >
        {title}
      </motion.h2>

      {/* Category Tabs */}
      <motion.div className="flex flex-wrap gap-3 mb-8">
        {categories.map((category, index) => (
          <motion.button
            key={category.name}
            onClick={() => setActiveCategory(index)}
            className={`px-4 py-2 rounded-full text-sm font-medium border ${
              activeCategory === index
                ? "bg-[#0C2019] text-[#88ff88] border-[#88ff88]"
                : "bg-transparent text-white/70 border-white/20 hover:border-white/40"
            }`}
            variants={tabVariants}
            animate={activeCategory === index ? "active" : "inactive"}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {category.name}
          </motion.button>
        ))}
      </motion.div>

      {/* Skills Grid */}
      <motion.div
        key={activeCategory}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-[rgba(10,32,25,0.5)] backdrop-blur-sm p-6 rounded-xl border border-[#88ff88]/20"
      >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {categories[activeCategory].skills.map((skill) => (
              <motion.div
                key={skill}
                className="flex items-center p-3 rounded-lg bg-[#0C2019]/60 border border-[#88ff88]/10 hover:border-[#88ff88]/30"
                whileHover={{
                  y: -5,
                  backgroundColor: "rgba(12, 40, 30, 0.8)",
                  transition: { duration: 0.2 }
                }}
              >
                <span className="font-medium text-white">{skill}</span>
              </motion.div>
            ))}
          </div>
      </motion.div>
    </motion.section>
  );
}